import { useState } from "react";
import { createGQLClient } from "../shared/gql-client";
import { ZodObject, ZodRawShape } from "zod";
import CrudService from "../root/general/crudGraphql/infrastructure/services/crudGraphqlService";
import { GraphqlFilterRoot } from "../root/general/crudGraphql/domain/models/graphqlModel";

export interface CrudModel {
  entity: string;
  prefix: string;
  schema: ZodObject<ZodRawShape>;
  page?: number;
  limit?: number;
  filters?: GraphqlFilterRoot;
}

export interface CrudDataModel {
  [key: string]: string | boolean | number | object;
}

export default function useCrud({
  entity,
  prefix,
  schema,
  page = 1,
  limit = 10,
  filters,
}: CrudModel) {
  const crudApi = new CrudService();

  const [loading, setLoading] = useState<boolean>(false);

  const [data, setData] = useState<CrudDataModel[]>([]);

  const client = createGQLClient();

  const getData = (
    overrideFilters?: GraphqlFilterRoot,
    customVariables?: { [key: string]: unknown } | undefined,
  ): Promise<CrudDataModel[]> => {
    crudApi.init(client, schema);
    setLoading(true);
    return new Promise((resolve, reject) => {
      crudApi
        .readAll(
          entity,
          prefix,
          page,
          limit,
          overrideFilters || filters,
          undefined,
          undefined,
          customVariables,
        )
        .then((response) => {
          const newData = (
            response as {
              data: CrudDataModel[];
            }
          ).data;
          setData(newData);
          setLoading(false);
          resolve(newData);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getOneData = (
    id: string,
    filters?: GraphqlFilterRoot,
  ): Promise<CrudDataModel> => {
    crudApi.init(client, schema);
    return new Promise((resolve, reject) => {
      crudApi
        .get(parseInt(id), prefix, undefined, filters)
        .then((response) => {
          const newData = (
            response as {
              data: CrudDataModel[];
            }
          ).data;
          setData((prevData) => [...prevData, ...newData]);
          resolve(newData[0]);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  return {
    data,
    getData,
    getOneData,
    loading,
    setLoading,
  };
}
